import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import {
    fetchDimensionsVAPT as fetchDimensionsVAPTService,
    updateDimensionweightages as updateDimensionWeightagesService,
    fetchVAPTSubDimensionsByDimensionId as fetchVAPTSubDimensionsByDimensionIdService,
    fetchDimensionsByCategory as fetchDimensionsByCategoryService,
 } from "services/modules/vapt/data";
import { fetchCloudTypes as fetchCloudTypesService } from "services/modules/vapt/alert";

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from 'config/Notification';
import Breadcrumbs from 'config/Breadcrumb';
import {
    Card,
    CardContent,
    Divider,
    Stepper,
    Step,
    StepLabel,
    Button,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    TextField,
    Grid,
    Typography,
    Box,
    CircularProgress,
    CardHeader
} from '@mui/material';



const DimensionWeightageConfigure = () => {
    // Notification
    const { openNotification } = useNotification();
    // Redux dispatch
    const dispatch = useDispatch();

    // State variables
    const [selectedDimensions, setSelectedDimensions] = useState([]); 
    const [subDimension, setSubDimension] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [cloudtype, setCloudtype] = useState(false);
    const [loading, setLoading] = useState(false); 
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCloudType, setSelectedCloudType] = useState('');
    const [selectedDimension, setSelectedDimension] = useState(''); 
    
    // Redux selectors
    const vaptDimensionsContainer = useSelector((state) => state.vaptData.vaptDimensions);
    const cloudTypesContainer = useSelector((state) => state.vaptAlert.cloudtypes );

    useEffect(() => {
        dispatch(fetchDimensionsVAPTService({}));
        dispatch(fetchCloudTypesService());
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
    
      useEffect(() => {
        setSelectedCategory('');
        setSelectedCloudType('');
        setSelectedDimension('');
        setSelectedDimensions([]);
        setSubDimension([]);
      },[activeStep]);
      
      // Fetches dimensions by category and cloud type
      const fetchDimensionsByCategoryCloudtype = async (category, cloudtype) => {
        try {
            setLoading(true);
            let params = {category: category, cloudtype: cloudtype};  
            const response = await fetchDimensionsByCategoryService({params});
            setSelectedDimensions(response);
            setLoading(false);
        } catch (error) {
            setLoading(false);
          openNotification("Error while fetching data", "error");
        }
      };
    
      // Fetch dimensions data based on the selected dimension type
    const fetchAllDimensionsByCategory = async (categoryId) => {
        try {
            setLoading(true);
            fetchDimensionsByCategoryCloudtype(categoryId, "");
            const filteredCategories = vaptDimensionsContainer.filter((category) => {
                return category._id === categoryId;
            });
            if (filteredCategories.length>0){
                if(filteredCategories[0].slug !== "cloud"){
                    setCloudtype(false);
                    setSelectedCloudType('');
                }
                else{
                    setCloudtype(true);   
                }
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            openNotification("Error while fetching data", "error");
        }
    };
    
    // Handles change in category
    const handleCategoryChange = (event) => {
        const categoryId = event.target.value;
        fetchAllDimensionsByCategory(categoryId);
        setSelectedCategory(categoryId);
    };
     
    // Handles change in cloud type
    const handleCloudTypeChange = (event) => {
        const cloudtypeId = event.target.value;
        setSelectedCloudType(cloudtypeId);
        fetchDimensionsByCategoryCloudtype(selectedCategory, cloudtypeId);    
    };
    
    // Fetches sub dimensions based on dimension id
    const fetchSubDimensionsByDimensionId = async (dimensionId) => {
        try {
            const response = await fetchVAPTSubDimensionsByDimensionIdService({ dimensionId });
            setSubDimension(response.data || []);
        } catch (error) {
            openNotification("Error while fetching sub-dimensions", 'error');
        }
    };
    
    // Function validates total weightage and throws an error if it doesnt satisfy necessary conditions
    const validateTotalWeightage = (weightages) => {
        const total = weightages.reduce((acc, curr) => acc + parseFloat(curr.weight), 0);
    const roundedTotal = Math.round(total * 100) / 100;
    if (roundedTotal < 10) {
        return "Total weightage must be exactly 10, should not be less than 10.";
    } else if (roundedTotal > 10) {
        return "Total weightage must be exactly 10, should not be greater than 10.";
    } else {
        return null;
    }
    };
    

    const validationSchemaStep1 = Yup.object({
        weightages: Yup.array()
            .of(
                Yup.object().shape({
                    id: Yup.string().required('Required'),
                    weight: Yup.number()
                        .required('Required')
                        .min(0, 'Min value is 0')
                        .max(10, 'Max value is 10')
                        .test(
                            'is-decimal',
                            'Weight must be a valid decimal (e.g., 0.2, 1.2, 1.55)',
                            value => /^\d+(\.\d{1,2})?$/.test(value)
                        ),
                })
            )
    });
    
    const validationSchemaStep2 = Yup.object({
        dimension: Yup.string().required('Dimension is required'),
        weightages: Yup.array()
            .of(
                Yup.object().shape({
                    id: Yup.string().required('Required'),
                    weight: Yup.number()
                        .required('Required')
                        .min(0, 'Min value is 0')
                        .max(10, 'Max value is 10')
                        .test(
                            'is-decimal',
                            'Weight must be a valid decimal (e.g., 0.2, 1.2, 1.55)',
                            value => /^\d+(\.\d{1,2})?$/.test(value)
                        ),
                })
            ),
    });
    

    const formikStep1 = useFormik({
        initialValues: {
            weightages: selectedDimensions.map((dimension) => ({
                label: dimension.label,
                slug: dimension.slug,
                id: dimension.id,
                weight: dimension.weight || 0,
            })),
        },
        validationSchema: validationSchemaStep1,
        onSubmit: async (values) => {
            const error = validateTotalWeightage(values.weightages);
            if (error) {
                alert(error);
                return;
            }
            try {

               let bodylist=[];
                // eslint-disable-next-line array-callback-return
                values.weightages.map((e)=>{
                    let obj = {
                        "id": e.id,
                        "weight": e.weight
                    }
                    bodylist.push(obj);
                })
                const response = await updateDimensionWeightagesService({ body: bodylist });
                if (response.status === 200) {
                    openNotification("Dimensions weightages updated successfully", 'success');
                   fetchDimensionsByCategoryCloudtype(selectedCategory, selectedCloudType);

                }
                setActiveStep(1);
            } catch (error) {
                openNotification("Error while updating dimensions", 'error');
            }
        },
        enableReinitialize: true, 
    });

    const formikStep2 = useFormik({
        initialValues: {
            dimension: selectedDimension, 
            weightages: subDimension.map((subDim) => ({
                label: subDim.label,
                slug: subDim.slug,
                id: subDim.id,
                weight: subDim.weight || 0,
            })),
        },
        validationSchema: validationSchemaStep2,
        onSubmit: async (values) => {
            const error = validateTotalWeightage(values.weightages);
            if (error) {
                alert(error);
                return;
            }
            try {
                let bodylist=[];
                // eslint-disable-next-line array-callback-return
                values.weightages.map((e)=>{
                    let obj = {
                        "id": e.id,
                        "weight": e.weight
                    }
                    bodylist.push(obj);
                })
                const response = await updateDimensionWeightagesService({ body:bodylist });
                if (response.status === 200) {
                    openNotification("Sub-Dimensions weightages updated successfully", 'success');
                    fetchSubDimensionsByDimensionId(formikStep2.values.dimension);
                }
            } catch (error) {
                openNotification("Error while updating sub-dimensions", 'error');
            }
        },
        enableReinitialize: true,
    });

    useEffect(() => {
        if (selectedDimension) {
            fetchSubDimensionsByDimensionId(selectedDimension);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDimension]);

    const handleDimensionChange = (event) => {
        const dimensionId = event.target.value;
        setSelectedDimension(dimensionId);
        formikStep2.setFieldValue('dimension', dimensionId); 
    };

    return (
        <div className="main-cont">
            <Card>
            <CardHeader
            title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                  <Breadcrumbs paths={['VAPT Configurator','Dimension Weightages']} />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
                <CardContent>
                    <Divider />
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {['Add Dimension Weightage', 'Add Sub-Dimension Weightage'].map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                    <Divider />

                    {loading ? (
                        <Box display="flex" justifyContent="center" mt={5}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {activeStep === 0 && (
                                <CardContent>
                                    <form onSubmit={formikStep1.handleSubmit}>
                                        <div className="row">
                                            <FormControl
                                            className="col-md-6 px-2"
                                            margin="normal"
                                            >
                                            <InputLabel>Category</InputLabel>
                                                <Select
                                                    name="category"
                                                    value={selectedCategory}
                                                    onChange={handleCategoryChange}
                                                >
                                                {vaptDimensionsContainer &&
                                                vaptDimensionsContainer.map((category) => (
                                                    <MenuItem
                                                    key={category._id}
                                                    value={category._id}
                                                    >
                                                    {category.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            </FormControl>
                                            { cloudtype && (<FormControl
                                            className="col-md-6 px-2"
                                            margin="normal"
                                            >
                                            <InputLabel>Cloud Types</InputLabel>
                                            <Select
                                                name="cloudtypes"
                                                value={selectedCloudType}
                                                onChange={handleCloudTypeChange}
                                            >
                                                {cloudTypesContainer &&
                                                cloudTypesContainer.map((category) => (
                                                    <MenuItem
                                                    key={category.id}
                                                    value={category.id}
                                                    >
                                                    {category.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            </FormControl>)}
                                        </div>
                                    {formikStep1.values.weightages.map((weightage, index) => (
                                        <Box key={weightage.id} mb={2}>
                                            {index % 2 === 0 && (
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={6}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={9}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        {formikStep1.values.weightages[index]?.label}
                                                    </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                    <TextField
                                                        label="Weight"
                                                        name={`weightages[${index}].weight`}
                                                        value={formikStep1.values.weightages[index]?.weight}
                                                        onChange={formikStep1.handleChange}
                                                        error={
                                                        formikStep1.touched.weightages?.[index]?.weight &&
                                                        Boolean(formikStep1.errors.weightages?.[index]?.weight)
                                                        }
                                                        helperText={
                                                        formikStep1.touched.weightages?.[index]?.weight &&
                                                        formikStep1.errors.weightages?.[index]?.weight
                                                        }
                                                        fullWidth
                                                        margin="normal"
                                                        inputProps={{ step: '0.1' }}
                                                    />
                                                    </Grid>
                                                </Grid>
                                                </Grid>
                                                {formikStep1.values.weightages[index + 1] && (
                                                <Grid item xs={6}>
                                                    <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={9}>
                                                        <Typography variant="body1" color="textSecondary">
                                                        {formikStep1.values.weightages[index + 1]?.label}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <TextField
                                                        label="Weight"
                                                        name={`weightages[${index + 1}].weight`}
                                                        value={formikStep1.values.weightages[index + 1]?.weight}
                                                        onChange={formikStep1.handleChange}
                                                        error={
                                                            formikStep1.touched.weightages?.[index + 1]?.weight &&
                                                            Boolean(formikStep1.errors.weightages?.[index + 1]?.weight)
                                                        }
                                                        helperText={
                                                            formikStep1.touched.weightages?.[index + 1]?.weight &&
                                                            formikStep1.errors.weightages?.[index + 1]?.weight
                                                        }
                                                        fullWidth
                                                        margin="normal"
                                                        inputProps={{ step: '0.1' }}
                                                        />
                                                    </Grid>
                                                    </Grid>
                                                </Grid>
                                                )}
                                            </Grid>
                                            )}
                                        </Box>
                                        ))}

                                        <Box display="flex" justifyContent="space-between" mt={3}>
                                            <Button color="primary" variant="contained" type="submit">
                                                Submit
                                            </Button>
                                            <Button color="primary" variant="contained" onClick={() => setActiveStep(1)}>
                                                Next
                                            </Button>
                                        </Box>

                                    </form>
                                </CardContent>
                            )}

                            {activeStep === 1 && (
                                <CardContent>
                                    <form onSubmit={formikStep2.handleSubmit}>
                                    <div className="row">
                                            <FormControl
                                            className="col-md-6 px-2"
                                            margin="normal"
                                            >
                                            <InputLabel>Category</InputLabel>
                                                <Select
                                                    name="category"
                                                    value={selectedCategory}
                                                    onChange={handleCategoryChange}
                                                >
                                                {vaptDimensionsContainer &&
                                                vaptDimensionsContainer.map((category) => (
                                                    <MenuItem
                                                    key={category._id}
                                                    value={category._id}
                                                    >
                                                    {category.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            </FormControl>
                                            { cloudtype && (<FormControl
                                            className="col-md-6 px-2"
                                            margin="normal"
                                            >
                                            <InputLabel>Cloud Types</InputLabel>
                                            <Select
                                                name="cloudtypes"
                                                value={selectedCloudType}
                                                onChange={handleCloudTypeChange}
                                            >
                                                {cloudTypesContainer &&
                                                cloudTypesContainer.map((category) => (
                                                    <MenuItem
                                                    key={category.id}
                                                    value={category.id}
                                                    >
                                                    {category.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            </FormControl>)}
                                        </div>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <FormControl fullWidth margin="normal">
                                                    <InputLabel>Dimension</InputLabel>
                                                    <Select
                                                        name="dimension"
                                                        value={formikStep2.values.dimension}
                                                        onChange={handleDimensionChange}
                                                        error={formikStep2.touched.dimension && Boolean(formikStep2.errors.dimension)}
                                                    >
                                                        {selectedDimensions.map((dimension) => (
                                                            <MenuItem key={dimension.id} value={dimension.id}>
                                                                {dimension.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        {formikStep2.values.weightages.map((weightage, index) => (
                                            <Box key={weightage.id} mb={2}>
                                                {index % 2 === 0 && (
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={6}>
                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={9}>
                                                        <Typography variant="body1" color="textSecondary">
                                                            {formikStep2.values.weightages[index]?.label}
                                                        </Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                        <TextField
                                                            label="Weight"
                                                            name={`weightages[${index}].weight`}
                                                            value={formikStep2.values.weightages[index]?.weight}
                                                            onChange={formikStep2.handleChange}
                                                            error={
                                                            formikStep2.touched.weightages?.[index]?.weight &&
                                                            Boolean(formikStep2.errors.weightages?.[index]?.weight)
                                                            }
                                                            helperText={
                                                            formikStep2.touched.weightages?.[index]?.weight &&
                                                            formikStep2.errors.weightages?.[index]?.weight
                                                            }
                                                            fullWidth
                                                            margin="normal"
                                                            inputProps={{ step: '0.1' }}
                                                        />
                                                        </Grid>
                                                    </Grid>
                                                    </Grid>
                                                    {formikStep2.values.weightages[index + 1] && (
                                                    <Grid item xs={6}>
                                                        <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={9}>
                                                            <Typography variant="body1" color="textSecondary">
                                                            {formikStep2.values.weightages[index + 1]?.label}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                            label="Weight"
                                                            name={`weightages[${index + 1}].weight`}
                                                            value={formikStep2.values.weightages[index + 1]?.weight}
                                                            onChange={formikStep2.handleChange}
                                                            error={
                                                                formikStep2.touched.weightages?.[index + 1]?.weight &&
                                                                Boolean(formikStep2.errors.weightages?.[index + 1]?.weight)
                                                            }
                                                            helperText={
                                                                formikStep2.touched.weightages?.[index + 1]?.weight &&
                                                                formikStep2.errors.weightages?.[index + 1]?.weight
                                                            }
                                                            fullWidth
                                                            margin="normal"
                                                            inputProps={{ step: '0.1' }}
                                                            />
                                                        </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    )}
                                                </Grid>
                                                )}
                                            </Box>
                                            ))}

                                        <Box display="flex" justifyContent="space-between" mt={3}>
                                            <Button color="primary" variant="contained" onClick={() => setActiveStep(0)}>
                                                Back
                                            </Button>
                                            <Button color="primary" variant="contained" type="submit">
                                                Submit
                                            </Button>
                                        </Box>
                                    </form>
                                </CardContent>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DimensionWeightageConfigure;
